import {inject, Injectable} from "@angular/core";
import {LoggingService} from "./logger/logging.service";
import {isDefined, isTrue} from "../libs/utils/epic.utils";
import {BehaviorSubject, distinctUntilChanged, Observable} from "rxjs";
import {Browser} from "../libs/enum/browser.enum";
import {filter} from "rxjs/operators";

@Injectable({providedIn: 'root'})
export class BrowserCheckService {

  // current Browsers: https://browsersl.ist/#q=defaults
  public static readonly CHROME_VERSION = 136;
  public static readonly FIREFOX_VERSION = 138;
  public static readonly EDGE_VERSION = 136;
  public static readonly IOS_VERSION = 16;
  public static readonly ANDROID_VERSION = 14;
  // Wie Angular selbst (@angular/build/.browserslistrc: Safari >= 16) - darunter ist der Bau nicht mehr lauffaehig zugesagt.
  public static readonly SAFARI_VERSION = 16;

  private static readonly CHROME_REGEX = /Chrome\/(\d+)/;
  private static readonly FIREFOX_REGEX = /Firefox\/(\d+)/;
  private static readonly EDGE_REGEX = /Edg\/(\d+)/;
  private static readonly IOS_REGEX = /OS (\d+)_(\d+)_?(\d+)?/;
  private static readonly ANDROID_REGEX = /Android (\d+)/;
  private static readonly SAFARI_REGEX = /Version\/(\d+).+Safari/;

  private readonly log: LoggingService = inject(LoggingService);

  private readonly supported = new BehaviorSubject<boolean>(true);
  private readonly browser = new BehaviorSubject<Browser | undefined>(undefined);
  private isChecked = false;

  public setSupportedForce(supported: boolean): void {
    this.log.warn('Browser check overwrite (force) with: {}', [supported]);
    this.isChecked = true;
    this.supported.next(supported);
  }

  public getSupported(): Observable<boolean> {
    if (!this.isChecked) {
      this.supported.next(this.isBrowserSupported());
    }
    return this.supported.asObservable();
  }

  public getBrowser(): Observable<Browser> {
    return this.browser.asObservable().pipe(
      filter((b) => isDefined(b)),
      distinctUntilChanged(),
    );
  }

  public isBrowser(browser: Browser): boolean {
    return this.browser.value === browser;
  }

  public isBrowserSupported(): boolean {
    // Check for query parameter override first
    const forceSupport = new URLSearchParams(window.location.search).get('browserSupport');
    if (isDefined(forceSupport)) {
      this.log.warn('Browser detection override with "{}"', [forceSupport]);
      return isTrue(forceSupport);
    }

    const userAgent = navigator.userAgent;
    // Chrome
    const chromeMatch = userAgent.match(BrowserCheckService.CHROME_REGEX);
    if (chromeMatch && parseInt(chromeMatch[1]) >= BrowserCheckService.CHROME_VERSION) {
      this.log.debug('Detect Browser "Chrome" with version "{}"', [chromeMatch[1]]);
      this.browser.next(Browser.CHROME);
      return true;
    }
    // Firefox
    const firefoxMatch = userAgent.match(BrowserCheckService.FIREFOX_REGEX);
    if (firefoxMatch && parseInt(firefoxMatch[1]) >= BrowserCheckService.FIREFOX_VERSION) {
      this.log.debug('Detect Browser "Firefox" with version "{}"', [firefoxMatch[1]]);
      this.browser.next(Browser.FIREFOX);
      return true;
    }
    // Edge Chromium
    const edgeMatch = userAgent.match(BrowserCheckService.EDGE_REGEX);
    if (edgeMatch && parseInt(edgeMatch[1]) >= BrowserCheckService.EDGE_VERSION) {
      this.log.debug('Detect Browser "Edge (Chromium)" with version "{}"', [edgeMatch[1]]);
      this.browser.next(Browser.EDGE);
      return true;
    }
    // iOS
    const iosMatch = userAgent.match(BrowserCheckService.IOS_REGEX);
    if (iosMatch && parseInt(iosMatch[1]) > BrowserCheckService.IOS_VERSION) {
      this.log.debug('Detect Browser "iOS" with version "{}"', [iosMatch[1]]);
      this.browser.next(Browser.IOS);
      return true;
    }
    // Android Check
    const androidMatch = userAgent.match(BrowserCheckService.ANDROID_REGEX);
    if (androidMatch && parseInt(androidMatch[1]) > BrowserCheckService.ANDROID_VERSION) {
      this.log.debug('Detect Browser "Android" with version "{}"', [androidMatch[1]]);
      this.browser.next(Browser.ANDROID);
      return true;
    }
    // Safari
    const safariMatch = userAgent.match(BrowserCheckService.SAFARI_REGEX);
    if (safariMatch && parseInt(safariMatch[1]) >= BrowserCheckService.SAFARI_VERSION) {
      this.log.debug('Detect Browser "Safari" with version "{}"', [safariMatch[1]]);
      this.browser.next(Browser.SAFARI);
      return true;
    }
    this.log.warn('Browser not supported: {}', [userAgent]);
    return false;
  }
}
